import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, rp, factorOf } from "@/lib/api";
import { PageHeader, Card, Field, Sel, MoneyOnly } from "@/components/common";
import { LineEditor, blankLine, toPayload } from "@/components/LineEditor";
import { PhotoUpload } from "@/components/PhotoUpload";
import { useAuth } from "@/context/AuthContext";

export default function TransferForm() {
  const { meta, myLocs, can, warehouse, itemById } = useAuth();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const sources = myLocs.filter((l) => (l.type === "warehouse" ? can("transfer.create") : can("transfer.outlet")));
  const [f, setF] = useState({ source_id: sources.find((l) => l.id === warehouse?.id)?.id || sources[0]?.id || "", destination_id: "", sender_id: "", notes: "", dispatch: true, delivery_request_id: null });
  const [lines, setLines] = useState([blankLine()]);
  const [photos, setPhotos] = useState([]);
  const [stock, setStock] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    if (!f.source_id) return;
    api.get("/stock", { params: { location_id: f.source_id } }).then(({ data }) => setStock(Object.fromEntries(data.rows.map((r) => [r.item_id, r]))));
  }, [f.source_id]);
  useEffect(() => {
    const dr = sp.get("dr");
    if (!dr) return;
    api.get("/delivery-requests").then(({ data }) => {
      const r = data.find((x) => x.id === dr);
      if (!r) return;
      setF((x) => ({ ...x, source_id: r.source_id, destination_id: r.location_id, delivery_request_id: r.id, notes: `Memenuhi ${r.code}` }));
      setLines(r.lines.map((l) => ({ item_id: l.item_id, qty: l.qty, unit: l.unit })));
    });
  }, [sp]);

  const est = useMemo(() => lines.reduce((s, l) => s + Number(l.qty || 0) * factorOf(itemById[l.item_id], l.unit) * (stock[l.item_id]?.unit_cost || 0), 0), [lines, stock, itemById]);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/transfers", { ...f, sender_id: f.sender_id || null, lines: toPayload(lines), photos: photos.map((p) => p.id) });
      toast.success(`${data.code} dibuat`);
      nav(`/transfers/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="max-w-4xl">
      <PageHeader title="Transfer baru" sub="Pilih asal, tujuan, barang dan pengirim. Biaya per unit mengikuti rata-rata tertimbang di lokasi asal (dengan aturan pembulatan)." />
      <Card className="p-4 space-y-4">
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Asal"><Sel testid="transfer-source-select" value={f.source_id} onChange={set("source_id")} options={sources.map((l) => ({ value: l.id, label: l.name }))} /></Field>
          <Field label="Tujuan"><Sel testid="transfer-destination-select" value={f.destination_id} onChange={set("destination_id")} placeholder="Pilih tujuan" options={meta.locations.filter((l) => l.id !== f.source_id).map((l) => ({ value: l.id, label: l.name }))} /></Field>
          <Field label="Pengirim / driver"><Sel testid="transfer-sender-select" value={f.sender_id} onChange={set("sender_id")} placeholder="Pilih pengirim" options={meta.senders.filter((s) => s.active !== false).map((s) => ({ value: s.id, label: s.name }))} /></Field>
        </div>
        <Field label="Barang"><LineEditor lines={lines} setLines={setLines} stock={stock} testid="transfer-line" /></Field>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Foto (opsional)"><PhotoUpload value={photos} onChange={setPhotos} label="Foto barang dikirim" testid="transfer-photo" /></Field>
          <Field label="Catatan"><textarea className="inp !h-[50px] py-2" value={f.notes} onChange={(e) => set("notes")(e.target.value)} data-testid="transfer-notes-input" /></Field>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t pt-4">
          <div className="flex items-center gap-4">
            <MoneyOnly><div className="text-sm">Estimasi nilai: <span className="num text-xl font-bold" data-testid="transfer-estimate">{rp(est)}</span></div></MoneyOnly>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.dispatch} onChange={(e) => set("dispatch")(e.target.checked)} data-testid="transfer-dispatch-checkbox" /> Langsung kirim</label>
          </div>
          <button disabled={busy} onClick={submit} className="h-10 px-5 rounded-md bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50" data-testid="transfer-submit-button">{f.dispatch ? "Kirim transfer" : "Simpan (disiapkan)"}</button>
        </div>
      </Card>
    </div>
  );
}
